import type { Address } from '../../../api/types';
import { formatOf } from './formats';

// An address as a register writes it (TF-33: VIES), split into the country's fields as far as it can
// be told apart: the postal code by the country's own pattern, the city beside it or just before it,
// the street first. What cannot be told apart stays on line 1. Pure, unit-tested in app.test.ts.

export function splitRegisteredAddress(text: string, country: string): Address {
  const pieces = text
    .split(/\n|,/)
    .map((piece) => piece.trim().replace(/\s+/g, ' '))
    .filter(Boolean);
  const whole: Address = { line1: pieces.join(', '), country };
  const zip = formatOf(country).zip;
  if (!zip || pieces.length < 2) return whole;
  const postal = new RegExp(`(?:^|\\s)(${zip})(?=\\s|$)`, 'i');
  // From the end: the last piece with a postal code in it.
  for (let at = pieces.length - 1; at >= 1; at -= 1) {
    const match = postal.exec(pieces[at]!);
    if (!match) continue;
    const beside = pieces[at]!.replace(match[1]!, '').trim();
    // "10117 Tallinn": the city beside the code; "RĪGA, LV-1010": the piece before it.
    const city = beside || pieces[at - 1]!;
    const street = pieces.slice(0, beside ? at : at - 1);
    if (street.length === 0) return whole;
    return { line1: street[0], ...(street.length > 1 ? { line2: street.slice(1).join(', ') } : {}), postalCode: match[1]!.toUpperCase(), city, country };
  }
  return whole;
}

/** VAT prefixes that are not the country's ISO code: Greece is EL, Northern Ireland XI. */
export const countryOfVatPrefix = (prefix: string) => ({ EL: 'GR', XI: 'GB' })[prefix] ?? prefix;
